import {
  useFinanceApi,
  useKernelApi,
  useServiceManagementApi,
  usePeopleManagementApi,
  useDeliveryApi,
  useDocumentsStorageApi,
  useRecruitmentApi,
} from 'infrastructure/api';
import useAuthenticationService from 'infrastructure/security/authentication/authenticationService';

export type Permission = string;

const COMPANY_PERMISSIONS_PATH = '/companies/1/permissions';
const PERMISSIONS_PATH = '/permissions';

const UPDATE = 'UPDATE';
const READ = 'READ';
const DELETE = 'DELETE';

const DEFAULT_PERMISSION_TIMEOUT = 30000; // 30 sec

const IGNORED_PERMISSIONS = [READ, UPDATE, DELETE];

const useDavinciAuthorizationService = () => {
  const { sendGetRequest } = useKernelApi();
  const { sendGetRequest: financeSendGetRequest } = useFinanceApi();
  const { sendGetRequest: smSendGetRequest } = useServiceManagementApi();
  const { sendGetRequest: pmSendGetRequest } = usePeopleManagementApi();
  const { sendGetRequest: deliverySendGetRequest } = useDeliveryApi();
  const { sendGetRequest: recruitmentGetRequest } = useRecruitmentApi();
  const { sendGetRequest: documentStorageSendGetRequest } = useDocumentsStorageApi();

  const { isAuthenticated } = useAuthenticationService();

  const getCompanyPermissions = async () => {
    const permissions = await sendGetRequest(COMPANY_PERMISSIONS_PATH, {
      timeout: DEFAULT_PERMISSION_TIMEOUT,
    });
    return permissions.json();
  };

  const getSMPermissions = async () => {
    const permissions = await smSendGetRequest(PERMISSIONS_PATH, {
      timeout: DEFAULT_PERMISSION_TIMEOUT,
    });
    return permissions.json();
  };

  const getFinancePermissions = async () => {
    const permissions = await financeSendGetRequest(PERMISSIONS_PATH, {
      timeout: DEFAULT_PERMISSION_TIMEOUT,
    });
    return permissions.json();
  };

  const getPMPermissions = async () => {
    const permissions = await pmSendGetRequest(PERMISSIONS_PATH, {
      timeout: DEFAULT_PERMISSION_TIMEOUT,
    });
    return permissions.json();
  };

  const getDeliveryPermissions = async () => {
    const permissions = await deliverySendGetRequest(PERMISSIONS_PATH, {
      timeout: DEFAULT_PERMISSION_TIMEOUT,
    });
    return permissions.json();
  };

  const getRecruitmentPermissions = async () => {
    const permissions = await recruitmentGetRequest(PERMISSIONS_PATH, {
      timeout: DEFAULT_PERMISSION_TIMEOUT,
    });
    return permissions.json();
  };

  const getDocumentsStoragePermissions = async () => {
    const permissions = await documentStorageSendGetRequest(PERMISSIONS_PATH, {
      timeout: DEFAULT_PERMISSION_TIMEOUT,
    });
    return permissions.json();
  };

  const permissionSources = [
    getCompanyPermissions,
    getSMPermissions,
    getFinancePermissions,
    getPMPermissions,
    getDeliveryPermissions,
    getDocumentsStoragePermissions,
    getRecruitmentPermissions,
  ];

  // ignore global READ, UPDATE, DELETE permissions
  const getGlobalPermissions = async () => {
    const isUserAuthenticated = await isAuthenticated();

    if (isUserAuthenticated) {
      const results = await Promise.allSettled(permissionSources.map(getPermissions => getPermissions()));
      const permissions = results.flatMap(result => (result.status === 'fulfilled' ? result.value : []));

      const filtered = permissions.filter((permission: Permission) => !IGNORED_PERMISSIONS.includes(permission));

      return { json: () => Promise.resolve(filtered) };
    } else {
      return { json: () => Promise.resolve(null) };
    }
  };

  return {
    getGlobalPermissions,
  };
};

export default useDavinciAuthorizationService;
